import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "@/lib/router-compat";

export interface MainHeading {
  id: string;
  text: string;
}

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

/**
 * Scans the main column for H2s, gives each a stable id derived from its text
 * (heading text itself is never modified) and returns the list so the jump
 * navigation and the mobile <select> stay in perfect sync with the page.
 */
export const useMainHeadings = (): MainHeading[] => {
  const [headings, setHeadings] = useState<MainHeading[]>([]);
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const collect = () => {
      const nodes = Array.from(document.querySelectorAll<HTMLHeadingElement>("main h2"));
      const seen = new Set<string>();
      const list: MainHeading[] = [];
      nodes.forEach((node) => {
        const text = (node.textContent || "").trim();
        if (!text) return;
        let id = slugify(text);
        if (!id) return;
        let n = 2;
        while (seen.has(id)) id = `${slugify(text)}-${n++}`;
        seen.add(id);
        node.id = id;
        node.style.scrollMarginTop = "110px";
        list.push({ id, text });
      });
      setHeadings(list);
    };
    collect();
    const t = window.setTimeout(collect, 300);
    return () => window.clearTimeout(t);
  }, [pathname]);

  return headings;
};

export const PageJumpNav = ({ headings }: { headings: MainHeading[] }) => {
  if (headings.length === 0) return null;
  return (
    <nav aria-label="On this page">
      <p className="font-condensed text-[13px] font-bold uppercase tracking-[0.14em] text-primary">On this page</p>
      <ul className="mt-3">
        {headings.map((h) => (
          <li key={h.id} className="border-b border-border/70 last:border-0">
            <a href={`#${h.id}`} className="block py-2 font-body text-[14px] leading-snug text-muted-foreground hover:text-primary">
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export const PageJumpSelect = ({ headings }: { headings: MainHeading[] }) => {
  const [value, setValue] = useState("");
  useEffect(() => setValue(""), [headings.length]);
  if (headings.length === 0) return null;
  return (
    <div className="lg:hidden">
      <label htmlFor="jump-to-section" className="block font-condensed text-[13px] font-bold uppercase tracking-[0.14em] text-primary">
        Jump to section
      </label>
      <select
        id="jump-to-section"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          const el = document.getElementById(e.target.value);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        className="mt-2 w-full rounded-[4px] border border-border bg-background px-3 py-2 font-body text-sm"
      >
        <option value="">Select a section…</option>
        {headings.map((h) => (
          <option key={h.id} value={h.id}>
            {h.text}
          </option>
        ))}
      </select>
    </div>
  );
};

export default PageJumpNav;
